<template>
  <div class="upload-file-list">
    fileList---{{ fileList }}
    <el-upload
      class="upload-demo"
      drag
      action="#"
      :auto-upload="false"
      :on-change="handleChange"
      :on-remove="handleRemove"
      :file-list="fileList"
      :accept="acceptTypes"
      :before-upload="beforeUpload"
      :show-file-list="false"
      multiple
    >
      <i class="el-icon-upload"></i>
      <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
      <!-- <div class="el-upload__tip" slot="tip">
        支持格式：docx, doc, pdf, xlsx, xls, pptx, ppt, txt, jpg, png, gif
      </div> -->
    </el-upload>

    <!-- 文件列表 -->
    <div class="file-list" v-if="fileList.length > 0">
      <div
        class="file-item"
        v-for="(file, index) in fileList"
        :key="file.uid || index"
      >
        <div class="file-info">
          <i :class="getFileIcon(file.name)"></i>
          <span class="file-name" :title="file.name">{{ file.name }}</span>
          <span class="file-size">{{ formatFileSize(file.size) }}</span>
        </div>
        <div class="file-actions">
          <el-button
            type="text"
            size="small"
            @click="handlePreview(file)"
            v-if="isPreviewable(file.name)"
          >
            预览
          </el-button>
          <el-button type="text" size="small" @click="handleDownload(file)">
            下载
          </el-button>
          <el-button
            type="text"
            size="small"
            class="delete-btn"
            @click="handleRemove(file, fileList)"
          >
            删除
          </el-button>
        </div>
        <!-- 上传进度 -->
        <el-progress
          v-if="file.status === 'uploading'"
          :percentage="file.percentage"
          :status="file.percentage === 100 ? 'success' : ''"
        ></el-progress>
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog
      :visible.sync="previewVisible"
      :title="previewFile.name"
      width="80%"
      top="5vh"
      :before-close="handleClosePreview"
    >
      <!-- PDF 预览 -->
      <div v-if="isPdf(previewFile.name)" class="pdf-preview">
        <iframe
          :src="previewUrl"
          width="100%"
          height="600px"
          frameborder="0"
        ></iframe>
      </div>
      <!-- 图片预览 -->
      <div v-else-if="isImage(previewFile.name)" class="image-preview">
        <img :src="previewUrl" style="max-width: 100%; max-height: 600px;" />
      </div>
      <!-- Office 文档预览 -->
      <div v-else-if="isOffice(previewFile.name)" class="office-preview">
        <iframe
          :src="previewUrl"
          width="100%"
          height="600px"
          frameborder="0"
        ></iframe>
      </div>
      <!-- 文本预览 -->
      <div v-else-if="isText(previewFile.name)" class="text-preview">
        <pre>{{ previewContent }}</pre>
      </div>
      <div v-else class="unsupported-preview">
        <p>该文件类型暂不支持在线预览</p>
        <el-button type="primary" @click="handleDownload(previewFile)">
          下载查看
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { postFilesUpload, getFilesPreview, getFilesDownload } from '@/api/file/file.js';

export default {
  name: 'UploadFileList',
  props: {
    // 允许上传的文件类型
    accept: {
      type: String,
      default: '.docx,.doc,.pdf,.xlsx,.xls,.pptx,.ppt,.txt,.jpg,.jpeg,.png,.gif'
    },
    // 最大文件大小（MB）
    maxSize: {
      type: Number,
      default: 50
    },
    // 最大上传数量
    limit: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      fileList: [],
      previewVisible: false,
      previewFile: {},
      previewUrl: '',
      previewContent: ''
    };
  },
  computed: {
    acceptTypes() {
      return this.accept;
    }
  },
  methods: {
    // 文件状态改变时的钩子
    handleChange(file, fileList) {
      // 检查文件类型
      const isValidType = this.checkFileType(file.name);
      if (!isValidType) {
        this.$message.error(`文件 ${file.name} 类型不支持！`);
        const index = fileList.indexOf(file);
        if (index > -1) {
          fileList.splice(index, 1);
        }
        return;
      }

      // 检查文件大小
      const isLtMax = file.size / 1024 / 1024 < this.maxSize;
      if (!isLtMax) {
        this.$message.error(`文件 ${file.name} 大小不能超过 ${this.maxSize}MB！`);
        const index = fileList.indexOf(file);
        if (index > -1) {
          fileList.splice(index, 1);
        }
        return;
      }

      
      
      // 自动上传
      if (file.status === 'ready') {
        this.uploadFile(file);
      }
    },

    // 上传文件到阿里云
    async uploadFile(file) {
      file.status = 'uploading';
      file.percentage = 0;

      try {
        const formData = new FormData();
        formData.append('file', file.raw);
        formData.append('fileName', file.name);

        const res = await postFilesUpload(formData);
        
        if (res.code === 200) {
          file.status = 'success';
          file.percentage = 100;
          file.response = res.data;
          this.fileList = res.data;
          this.$message.success(`${file.name} 上传成功！`);
          this.$emit('upload-success', res.data, file);
        } else {
          throw new Error(res.msg || '上传失败');
        }
      } catch (error) {
        file.status = 'error';
        this.$message.error(`${file.name} 上传失败：${error.message}`);
        this.$emit('upload-error', error, file);
      }
    },

    // 删除文件
    handleRemove(file, fileList) {
      const index = this.fileList.indexOf(file);
      if (index > -1) {
        this.fileList.splice(index, 1);
      }
      this.$emit('remove', file);
    },

    // 预览文件
    async handlePreview(file) {
      this.previewFile = file;
      this.previewVisible = true;

      try {
        if (file.response && file.response.fileId) {
          // 获取预览URL
          const res = await getFilesPreview(file.response.fileId);
          if (res.code === 200) {
            this.previewUrl = res.data;
          }
        } else if (file.raw) {
          // 本地文件预览
          if (this.isImage(file.name)) {
            this.previewUrl = URL.createObjectURL(file.raw);
          } else if (this.isText(file.name)) {
            const reader = new FileReader();
            reader.onload = (e) => {
              this.previewContent = e.target.result;
            };
            reader.readAsText(file.raw);
          }
        }
      } catch (error) {
        this.$message.error('预览失败：' + error.message);
      }
    },

    // 下载文件
    async handleDownload(file) {
      try {
        if (file.response && file.response.fileId) {
          const res = await getFilesDownload(file.response.fileId);
          if (res.code === 200) {
            const link = document.createElement('a');
            link.href = res.data;
            link.download = file.name;
            link.click();
          }
        } else if (file.raw) {
          // 本地文件下载
          const link = document.createElement('a');
          link.href = URL.createObjectURL(file.raw);
          link.download = file.name;
          link.click();
        }
      } catch (error) {
        this.$message.error('下载失败：' + error.message);
      }
    },

    // 关闭预览
    handleClosePreview() {
      this.previewVisible = false;
      this.previewUrl = '';
      this.previewContent = '';
    },

    // 上传前的钩子
    beforeUpload(file) {
      return true;
    },

    // 检查文件类型
    checkFileType(fileName) {
      if (!fileName) return false;
      const ext = fileName.split('.').pop().toLowerCase();
      const allowedExts = this.accept.split(',').map(t => t.replace('.', '').trim());
      return allowedExts.includes(ext);
    },

    // 获取文件图标
    getFileIcon(fileName) {
      if (!fileName) return 'el-icon-document';
      const ext = fileName.split('.').pop().toLowerCase();
      const iconMap = {
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'pdf': 'el-icon-document-copy',
        'xls': 'el-icon-s-data',
        'xlsx': 'el-icon-s-data',
        'ppt': 'el-icon-data-board',
        'pptx': 'el-icon-data-board',
        'txt': 'el-icon-tickets',
        'jpg': 'el-icon-picture',
        'jpeg': 'el-icon-picture',
        'png': 'el-icon-picture',
        'gif': 'el-icon-picture'
      };
      return iconMap[ext] || 'el-icon-document';
    },

    // 格式化文件大小
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB';
      } else {
        return (size / 1024 / 1024).toFixed(2) + ' MB';
      }
    },

    // 是否可预览
    isPreviewable(fileName) {
      if (!fileName) return false;
      return this.isPdf(fileName) || 
             this.isImage(fileName) || 
             this.isOffice(fileName) || 
             this.isText(fileName);
    },

    // 是否是PDF
    isPdf(fileName) {
      if (!fileName) return false;
      return fileName.toLowerCase().endsWith('.pdf');
    },

    // 是否是图片
    isImage(fileName) {
      if (!fileName) return false;
      const exts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      return exts.some(ext => fileName.toLowerCase().endsWith(ext));
    },

    // 是否是Office文档
    isOffice(fileName) {
      if (!fileName) return false;
      const exts = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
      return exts.some(ext => fileName.toLowerCase().endsWith(ext));
    },

    // 是否是文本文件
    isText(fileName) {
      if (!fileName) return false;
      const exts = ['.txt', '.md', '.json', '.xml', '.html', '.css', '.js'];
      return exts.some(ext => fileName.toLowerCase().endsWith(ext));
    },

    // 获取文件列表（供外部调用）
    getFileList() {
      return this.fileList;
    },

    // 清空文件列表（供外部调用）
    clearFileList() {
      this.fileList = [];
    },

    // 设置文件列表（供外部调用，用于回显）
    setFileList(list) {
      this.fileList = list.map(item => ({
        name: item.fileName || item.name,
        url: item.fileUrl || item.url,
        size: item.fileSize || item.size,
        response: item,
        status: 'success'
      }));
    }
  }
};
</script>

<style lang="less" scoped>
.upload-file-list {
  .upload-demo {
    width: 100%;
  }

  .file-list {
    margin-top: 20px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 10px;

    .file-item {
      padding: 10px;
      border-bottom: 1px solid #ebeef5;
      position: relative;

      &:last-child {
        border-bottom: none;
      }

      .file-info {
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          font-size: 20px;
          color: #409eff;
        }

        .file-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #606266;
        }

        .file-size {
          color: #909399;
          font-size: 12px;
        }
      }

      .file-actions {
        margin-top: 8px;
        display: flex;
        gap: 15px;

        .delete-btn {
          color: #f56c6c;
        }
      }

      .el-progress {
        margin-top: 8px;
      }
    }
  }

  .pdf-preview,
  .office-preview {
    width: 100%;
    height: 600px;
  }

  .image-preview {
    text-align: center;
    
    img {
      max-width: 100%;
      max-height: 600px;
    }
  }

  .text-preview {
    max-height: 600px;
    overflow: auto;
    background: #f5f7fa;
    padding: 20px;
    border-radius: 4px;

    pre {
      margin: 0;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
  }

  .unsupported-preview {
    text-align: center;
    padding: 50px;

    p {
      color: #909399;
      margin-bottom: 20px;
    }
  }
}
</style>
